import { useChat } from "@ai-sdk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { type AgentChatMessage } from "@fitness-app/data-models";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { mapStoredMessage } from "~/modules/Sales/SalesAgent/messageUtils";
import { supabase } from "~/store/initializeStore";
import { STUDIO_SEGMENTS_QUERY_KEY } from "./useStudioAds";
import { STUDIO_CLIPS_QUERY_KEY } from "./useStudioData";

/** `agent_chat_session.agentType` of the Reklamy chat (mirrors the client-web route). */
const agentType = "studio_ads_agent";
const latestSessionKey = (requestId: string | null) => ["studioAdsChatSession", requestId];
const sessionMessagesKey = (sessionId: string | null) => ["studioAdsChatMessages", sessionId];

/**
 * Rozmowa z agentem reklam (etap G) na tym samym transporcie, co agent planów treningowych:
 * odpowiedź streamuje się z `studio-ads-chat`, a historia leży w `agent_chat_session`
 * (jedna rozmowa na przebieg Studio, `contextId` = id przebiegu). Po wejściu na nagranie
 * wznawiamy ostatnią rozmowę o nim; „Nowa rozmowa" zaczyna kolejną. Po każdej odpowiedzi
 * odświeżamy segmenty i warianty, bo agent mógł je właśnie złożyć albo wysłać do renderu.
 */
export const useStudioAdsChat = (requestId: string | null) => {
  const queryClient = useQueryClient();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  // Sessions whose messages are already on screen (streamed here or hydrated once) —
  // the stored-messages query must not overwrite them.
  const hydratedSessionIdRef = useRef<string | null>(null);
  // The request whose latest session was already resumed (or deliberately skipped by reset).
  const resumedRequestIdRef = useRef<string | null>(null);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `${getClientWebApiBaseUrl()}/api/trainers/ai/studio-ads-chat`,
        fetch: async (request, init) => {
          const response = await fetch(request, init);
          const id = response.headers.get("X-Agent-Chat-Session-Id");
          if (id && id !== sessionIdRef.current) {
            sessionIdRef.current = id;
            hydratedSessionIdRef.current = id;
            setSessionId(id);
          }
          return response;
        },
        prepareSendMessagesRequest: async ({ api, body, headers, credentials, messages }) => {
          const token = (await supabase.auth.getSession()).data.session?.access_token;
          const latestMessage = messages.at(-1);
          return {
            api,
            credentials,
            headers: { ...headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: {
              ...body,
              requestId,
              sessionId: sessionIdRef.current ?? undefined,
              messages: latestMessage ? [latestMessage] : [],
            },
          };
        },
      }),
    [requestId],
  );

  const { messages, sendMessage, setMessages, status, stop, error, clearError } = useChat<UIMessage>({
    transport,
    experimental_throttle: 50,
    onFinish: () => {
      void queryClient.invalidateQueries(STUDIO_CLIPS_QUERY_KEY);
      void queryClient.invalidateQueries(STUDIO_SEGMENTS_QUERY_KEY);
      void queryClient.invalidateQueries({ queryKey: latestSessionKey(requestId) });
    },
  });
  const busy = status === "submitted" || status === "streaming";

  const latestSession = useQuery({
    queryKey: latestSessionKey(requestId),
    enabled: Boolean(requestId),
    queryFn: async () => {
      const { data, error: queryError } = await supabase
        .from("agent_chat_session")
        .select("id")
        .eq("agentType", agentType)
        .eq("contextId", requestId!)
        .order("lastMessageAt", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (queryError) throw new Error(queryError.message);
      return data?.id ?? null;
    },
  });

  // A different recording = a different conversation.
  useEffect(() => {
    sessionIdRef.current = null;
    hydratedSessionIdRef.current = null;
    resumedRequestIdRef.current = null;
    setSessionId(null);
    setMessages([]);
    clearError();
  }, [clearError, requestId, setMessages]);

  useEffect(() => {
    if (!requestId || latestSession.data === undefined || resumedRequestIdRef.current === requestId) return;
    resumedRequestIdRef.current = requestId;
    if (!latestSession.data || sessionIdRef.current) return;
    sessionIdRef.current = latestSession.data;
    setSessionId(latestSession.data);
  }, [latestSession.data, requestId]);

  const storedMessages = useQuery({
    queryKey: sessionMessagesKey(sessionId),
    enabled: Boolean(sessionId) && hydratedSessionIdRef.current !== sessionId,
    queryFn: async () => {
      const { data, error: queryError } = await supabase
        .from("agent_chat_message")
        .select("*")
        .eq("sessionId", sessionId!)
        .order("createdAt", { ascending: true });
      if (queryError) throw new Error(queryError.message);
      return (data ?? []) as AgentChatMessage[];
    },
  });

  useEffect(() => {
    if (!sessionId || !storedMessages.data || busy || hydratedSessionIdRef.current === sessionId) return;
    hydratedSessionIdRef.current = sessionId;
    setMessages(storedMessages.data.map(mapStoredMessage));
  }, [busy, sessionId, setMessages, storedMessages.data]);

  const send = useCallback(
    (text: string) => {
      const content = text.trim();
      if (!content || busy || !requestId) return;
      clearError();
      void sendMessage({ id: crypto.randomUUID(), role: "user", parts: [{ type: "text", text: content }] });
    },
    [busy, clearError, requestId, sendMessage],
  );

  const reset = useCallback(() => {
    if (busy) stop();
    sessionIdRef.current = null;
    hydratedSessionIdRef.current = null;
    resumedRequestIdRef.current = requestId;
    setSessionId(null);
    setMessages([]);
    clearError();
  }, [busy, clearError, requestId, setMessages, stop]);

  return { messages, send, reset, stop, status, busy, error, loading: latestSession.isLoading };
};
